import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Star, Trash2, Pencil, X, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import {
  businessesService,
  PAYOUT_METHODS,
  type PayoutDetail,
  type PayoutDetailInput,
} from '@services/businesses.service';

const methodLabel = (code: string) => PAYOUT_METHODS.find((m) => m.code === code)?.label ?? code;

function summarise(d: PayoutDetail): string {
  const v = d.details || {};
  switch (d.payoutMethod) {
    case 'mpesa': return v.phone ?? '';
    case 'mpesa_paybill': return `Paybill ${v.paybill_number ?? ''} · Acct ${v.account_number ?? ''}`;
    case 'mpesa_till': return `Till ${v.till_number ?? ''}`;
    case 'bank_transfer': return `${v.bank_name ?? ''} · ${v.account_number ?? ''} · ${v.account_name ?? ''}`;
    default: return Object.values(v).join(' · ');
  }
}

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

const input = 'w-full rounded-xl border border-[var(--border-default)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none';

export function BusinessPayoutPanel({ businessId }: { businessId: string }) {
  const qc = useQueryClient();
  const key = ['business-payouts', businessId];
  const { data: methods = [], isLoading } = useQuery({ queryKey: key, queryFn: () => businessesService.listPayouts(businessId) });
  const [editing, setEditing] = useState<PayoutDetail | null>(null);
  const [adding, setAdding] = useState(false);

  const setDefault = useMutation({
    mutationFn: (id: string) => businessesService.setDefaultPayout(businessId, id),
    onSuccess: () => { toast.success('Default updated.'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error(apiMessage(e, 'Could not update default.')),
  });
  const archive = useMutation({
    mutationFn: (id: string) => businessesService.archivePayout(businessId, id),
    onSuccess: () => { toast.success('Removed.'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error(apiMessage(e, 'Could not remove.')),
  });

  return (
    <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
            <Wallet size={16} className="text-[var(--text-accent)]" /> Payout destinations
          </h3>
          <p className="mt-1 text-[13px] text-[var(--text-muted)]">Where this business receives its earnings. The default is used for withdrawals.</p>
        </div>
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--text-primary)] px-3 py-1.5 text-[13px] font-semibold text-[var(--bg-surface)]">
          <Plus size={14} /> Add
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-[13px] text-[var(--text-muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : methods.length === 0 ? (
        <p className="py-6 text-[13px] text-[var(--text-muted)]">No payout destination on file for this business.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {methods.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-subtle)] px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">{methodLabel(d.payoutMethod)}</span>
                  {d.isDefault && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--state-success-bg)] px-2 py-0.5 text-[11px] font-semibold text-[var(--state-success)]"><Star size={11} /> Default</span>}
                </div>
                <p className="truncate text-[13px] text-[var(--text-muted)]">{d.label ? `${d.label} · ` : ''}{summarise(d)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {!d.isDefault && <button onClick={() => setDefault.mutate(d.id)} title="Make default" className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-accent)] hover:bg-[var(--bg-base)]"><Star size={15} /></button>}
                <button onClick={() => setEditing(d)} title="Edit" className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)]"><Pencil size={15} /></button>
                <button onClick={() => { if (window.confirm('Remove this payout destination?')) archive.mutate(d.id); }} title="Remove" className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--state-danger)] hover:bg-[var(--state-danger-bg)]"><Trash2 size={15} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(adding || editing) && (
        <PayoutModal businessId={businessId} existing={editing} isFirst={methods.length === 0}
          onSaved={() => qc.invalidateQueries({ queryKey: key })}
          onClose={() => { setAdding(false); setEditing(null); }} />
      )}
    </div>
  );
}

function PayoutModal({ businessId, existing, isFirst, onSaved, onClose }: {
  businessId: string; existing: PayoutDetail | null; isFirst: boolean; onSaved: () => void; onClose: () => void;
}) {
  const [method, setMethod] = useState(existing?.payoutMethod ?? 'mpesa');
  const [details, setDetails] = useState<Record<string, string>>(existing?.details ?? {});
  const [label, setLabel] = useState(existing?.label ?? '');
  const [makeDefault, setMakeDefault] = useState(existing?.isDefault ?? isFirst);

  const def = PAYOUT_METHODS.find((m) => m.code === method)!;
  const missing = def.fields.find((f) => !details[f.key]?.trim());

  const save = useMutation({
    mutationFn: () => {
      const body: PayoutDetailInput = { payoutMethod: method, details, label: label.trim() || undefined, makeDefault };
      return existing ? businessesService.updatePayout(businessId, existing.id, body) : businessesService.addPayout(businessId, body);
    },
    onSuccess: () => { toast.success(existing ? 'Updated.' : 'Added.'); onSaved(); onClose(); },
    onError: (e) => toast.error(apiMessage(e, 'Could not save.')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{existing ? 'Edit payout destination' : 'Add payout destination'}</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">Method</label>
            <select value={method} onChange={(e) => { setMethod(e.target.value); setDetails({}); }} className={input}>
              {PAYOUT_METHODS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
            </select>
          </div>
          {def.fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">{f.label}</label>
              <input value={details[f.key] ?? ''} onChange={(e) => setDetails((d) => ({ ...d, [f.key]: e.target.value }))} className={input} />
            </div>
          ))}
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">Label (optional)</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} className={input} />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
            <input type="checkbox" checked={makeDefault} disabled={isFirst} onChange={(e) => setMakeDefault(e.target.checked)} />
            Default payout destination
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-[var(--text-muted)]">Cancel</button>
            <button disabled={!!missing || save.isPending} onClick={() => save.mutate()} className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-sm font-semibold text-[var(--bg-surface)] disabled:opacity-50">
              {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {missing ? `Enter ${missing.label}` : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
